import React, { useState, useEffect, useRef } from 'react';
import { 
  Camera, User, CheckCircle, AlertCircle, RefreshCw, X, 
  Edit, Save, Phone, Home, Briefcase
} from 'lucide-react';
import * as faceapi from 'face-api.js';
import { api } from '../utils/api';
import { getCameraConstraints, getSimpleCameraConstraints } from '../utils/helpers';

const academicPositions = [
  'ครูผู้ช่วย',
  'ครู',
  'ครูชำนาญการ',
  'ครูชำนาญการพิเศษ',
  'ครูเชี่ยวชาญ',
  'ครูเชี่ยวชาญพิเศษ',
  'ครูอัตราจ้าง',
  'ลูกจ้าง(ชั่วคราว)',
];

const MyProfile = ({ user }) => {
  const [employee, setEmployee] = useState(null);
  const [editedEmployee, setEditedEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [showFaceModal, setShowFaceModal] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    loadEmployeeData();
  }, [user]);

  const loadEmployeeData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // ⚠️ Backend จะส่งกลับมาเฉพาะรายชื่อและแผนกของโรงเรียนตัวเองอัตโนมัติ
      const [empData, deptData] = await Promise.all([
        api.getEmployees(),
        api.getDepartments()
      ]);
      
      const currentEmployee = empData.find(emp => emp.employee_id === user.employee_id);
      
      if (currentEmployee) {
        const employeeWithStatus = {
          ...currentEmployee,
          face_status: currentEmployee.has_registered_face == 1 ? 'ลงทะเบียนแล้ว' : 'ยังไม่ลงทะเบียน'
        };

        setEmployee(employeeWithStatus);
        setEditedEmployee(employeeWithStatus);
      }
      setDepartments(deptData);
      
    } catch (error) {
      console.error('Error loading employee data:', error);
      window.alert('ไม่สามารถโหลดข้อมูลได้: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedEmployee({
      ...editedEmployee,
      [name]: value
    });
  };

  const handleSaveProfile = async () => {
    if (!editedEmployee) return;
    
    const updateData = {
      employee_id: editedEmployee.employee_id,
      first_name: editedEmployee.first_name,
      last_name: editedEmployee.last_name,
      phone: editedEmployee.phone,
      address: editedEmployee.address,
      academic_position: editedEmployee.academic_position,
      department: editedEmployee.department,
    };

    try {
      const data = await api.updateEmployee(updateData);
      if (data.success) {
        setEmployee(editedEmployee);
        setIsEditing(false);
        window.alert('บันทึกข้อมูลเรียบร้อยแล้ว');
      } else {
        window.alert('ไม่สามารถบันทึกข้อมูลได้: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      window.alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditedEmployee(employee);
    setIsEditing(false);
  };

  const startCamera = async () => {
    const isSecureContext = window.location.protocol === 'https:' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';

    if (!isSecureContext) {
      window.alert('ระบบต้องการ HTTPS เพื่อใช้งานกล้องบนมือถือ กรุณาใช้งานผ่าน HTTPS');
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      window.alert('เบราว์เซอร์ของคุณไม่รองรับการใช้งานกล้อง');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia(getCameraConstraints());
      
      if (!videoRef.current) {
        console.error('Video ref not found');
        return;
      }

      videoRef.current.srcObject = stream;
      setCameraActive(true);
      setRegistrationResult(null);
      
    } catch (error) {
      console.error('Camera error details:', error);
      
      let errorMessage = 'ไม่สามารถเข้าถึงกล้องได้';
      
      if (error.name === 'OverconstrainedError') {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(getSimpleCameraConstraints());
          videoRef.current.srcObject = stream;
          setCameraActive(true);
          setRegistrationResult(null);
          return;
        } catch (retryError) {
          errorMessage = 'การตั้งค่ากล้องไม่รองรับ กรุณาลองในเบราว์เซอร์อื่น';
        }
      } else if (error.name === 'NotAllowedError') {
        errorMessage = 'กรุณาอนุญาตการใช้งานกล้องในเบราว์เซอร์:\n\n' +
                      '• Chrome: กดไอคอนกล้อง ข้างๆ URL แล้วเลือก "อนุญาต"\n' +
                      '• Safari: ตั้งค่า > กล้อง > อนุญาต\n' +
                      '• Firefox: กดไอคอนโล่ ข้างๆ URL';
      }
      
      window.alert(errorMessage);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setCameraActive(false);
      setRegistering(false);
    }
  };

  const registerFace = async () => {
    if (!employee || registering) return;

    setRegistering(true);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const displaySize = { width: video.videoWidth, height: video.videoHeight };
      faceapi.matchDimensions(canvas, displaySize);
      
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.SsdMobilenetv1Options())
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (detection) {
        const resizedDetection = faceapi.resizeResults(detection, displaySize);
        
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        faceapi.draw.drawDetections(canvas, [resizedDetection]);
        faceapi.draw.drawFaceLandmarks(canvas, [resizedDetection]);
        
        const data = await api.registerFace({
          employee_id: employee.employee_id,
          face_encodings: Array.from(detection.descriptor)
        });
        
        if (data.success) {
          setRegistrationResult({
            success: true,
            message: 'ลงทะเบียนใบหน้าเรียบร้อยแล้ว\nตอนนี้คุณสามารถใช้งานระบบสแกนหน้าเข้างานได้'
          });
          stopCamera();
          setTimeout(() => {
            loadEmployeeData();
          }, 2000);
        } else {
          setRegistrationResult({
            success: false,
            message: data.error || 'ไม่สามารถลงทะเบียนได้'
          });
        }
      } else {
        window.alert('ไม่พบใบหน้าในกรอบ กรุณาลองใหม่\n\nคำแนะนำ:\n• หันหน้าตรงกล้อง\n• อยู่ในที่ที่มีแสงสว่างเพียงพอ\n• ห่างจากกล้องประมาณ 30-50 ซม.');
      }
      
    } catch (error) {
      console.error('Error registering face:', error);
      setRegistrationResult({
        success: false,
        message: 'เกิดข้อผิดพลาด: ' + error.message
      });
    } finally {
      setRegistering(false);
    }
  };

  const openFaceModal = () => {
    setShowFaceModal(true);
    setRegistrationResult(null);
  };

  const closeFaceModal = () => {
    stopCamera();
    setShowFaceModal(false);
    setRegistrationResult(null);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบข้อมูลผู้ใช้</h3>
        <p className="mt-1 text-sm text-gray-500">กรุณาติดต่อผู้ดูแลระบบ</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          ข้อมูลของฉัน
        </h1>
        <p className="text-gray-600 mt-2">
          จัดการข้อมูลส่วนตัวและข้อมูลใบหน้าของคุณ
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Profile Info Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <User className="h-6 w-6 mr-2 text-blue-500" />
              ข้อมูลบุคลากร
            </h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-blue-500 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
              >
                <Edit className="h-4 w-4" />
                <span>แก้ไข</span>
              </button>
            )}
          </div>
          
          <div className="p-6 space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-10 w-10 text-blue-500" />
                </div>
              </div>
              
              <div className="flex-1 space-y-3">
                {!isEditing ? (
                  // --- โหมดแสดงผล ---
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500">รหัสบุคลากร</label>
                      <p className="text-lg font-semibold text-gray-900">{employee.employee_id}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">ชื่อ-นามสกุล</label>
                      <p className="text-lg font-semibold text-gray-900">{employee.first_name} {employee.last_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 flex items-center"><Phone className="h-4 w-4 mr-1 text-gray-400"/> เบอร์โทรศัพท์</label>
                      <p className="text-lg font-semibold text-gray-900">{employee.phone || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 flex items-center"><Briefcase className="h-4 w-4 mr-1 text-gray-400"/> ตำแหน่ง</label>
                      <p className="text-lg font-semibold text-gray-900">{employee.academic_position || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">แผนก/กลุ่มสาระฯ</label>
                      <p className="text-lg font-semibold text-gray-900">{employee.department || '-'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-500 flex items-center"><Home className="h-4 w-4 mr-1 text-gray-400"/> ที่อยู่</label>
                      <p className="text-base text-gray-900">{employee.address || '-'}</p>
                    </div>
                  </div>
                ) : (
                  // --- โหมดแก้ไข ---
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">รหัสพนักงาน</label>
                      <input type="text" value={editedEmployee.employee_id} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Briefcase className="h-4 w-4 mr-1 text-gray-400"/> ตำแหน่ง</label>
                      <select
                        name="academic_position"
                        value={editedEmployee.academic_position || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- ไม่ระบุ --</option>
                        {academicPositions.map(pos => (
                          <option key={pos} value={pos}>
                            {pos}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ</label>
                      <input type="text" name="first_name" value={editedEmployee.first_name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">นามสกุล</label>
                      <input type="text" name="last_name" value={editedEmployee.last_name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Phone className="h-4 w-4 mr-1 text-gray-400"/> เบอร์โทรศัพท์</label>
                      <input type="tel" name="phone" value={editedEmployee.phone || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">แผนก/กลุ่มสาระฯ</label>
                      <select name="department" value={editedEmployee.department || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">-- ไม่ระบุ --</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.department_name}>{dept.department_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Home className="h-4 w-4 mr-1 text-gray-400"/> ที่อยู่ (ย่อ)</label>
                      <textarea rows="2" name="address" value={editedEmployee.address || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
                    </div>
                  </div>
                )}
                
                <div className="pt-3 border-t">
                  {!isEditing ? (
                    <>
                      <label className="text-sm text-gray-500">สถานะใบหน้า</label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          employee.face_status === 'ลงทะเบียนแล้ว' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {employee.face_status === 'ลงทะเบียนแล้ว' ? (
                            <CheckCircle className="h-4 w-4 mr-1" />
                          ) : (
                            <AlertCircle className="h-4 w-4 mr-1" />
                          )}
                          {employee.face_status}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex space-x-3 justify-end">
                      <button
                        onClick={handleCancelEdit}
                        className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
                      >
                        ยกเลิก
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 flex items-center space-x-2 transition-colors"
                      >
                        <Save className="h-4 w-4" />
                        <span>บันทึก</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Face Registration Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Camera className="h-6 w-6 mr-2 text-blue-500" />
              {employee.face_status === 'ลงทะเบียนแล้ว' ? 'อัพเดทใบหน้า' : 'ลงทะเบียนใบหน้า'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {employee.face_status === 'ลงทะเบียนแล้ว' 
                ? 'คุณสามารถอัพเดทข้อมูลใบหน้าได้หากต้องการเปลี่ยนแปลง'
                : 'กรุณาลงทะเบียนใบหน้าเพื่อใช้งานระบบสแกนหน้าเข้างาน'}
            </p>
          </div>
          
          <div className="p-6">
            <button
              onClick={openFaceModal}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 flex items-center justify-center space-x-2 transition-colors"
            >
              <Camera className="h-5 w-5" />
              <span>{employee.face_status === 'ลงทะเบียนแล้ว' ? 'อัพเดทใบหน้า' : 'เริ่มลงทะเบียนใบหน้า'}</span>
            </button>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-sm font-medium text-blue-900 mb-2">คำแนะนำ:</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• หันหน้าตรงกล้องและอยู่ในที่ที่มีแสงสว่างเพียงพอ</li>
                <li>• ถอดแว่นตาและหน้ากากออก (ถ้ามี)</li>
                <li>• ห่างจากกล้องประมาณ 30-50 ซม.</li>
                <li>• รักษาท่าหยุดนิ่งขณะลงทะเบียน</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Reload Button */}
      <div className="text-center">
        <button
          onClick={loadEmployeeData}
          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>รีเฟรชข้อมูล</span>
        </button>
      </div>

      {/* Face Registration Modal */}
      {showFaceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <Camera className="h-6 w-6 mr-2" />
                ลงทะเบียนใบหน้า - {employee.first_name} {employee.last_name}
              </h2>
              <button
                onClick={closeFaceModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-6" style={{ aspectRatio: '4/3' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  style={{ 
                    display: cameraActive ? 'block' : 'none',
                    transform: 'scaleX(-1)'
                  }}
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      videoRef.current.play();
                    }
                  }}
                />
                
                <canvas 
                  ref={canvasRef} 
                  className="absolute inset-0 pointer-events-none"
                  style={{ 
                    display: cameraActive ? 'block' : 'none',
                    transform: 'scaleX(-1)'
                  }}
                />
                
                {!cameraActive && !registrationResult && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Camera className="h-20 w-20 text-gray-400 mx-auto mb-4" />
                      <p className="text-xl text-gray-500">พร้อมลงทะเบียนใบหน้า</p>
                      <p className="text-sm text-gray-400 mt-2">กดปุ่มเพื่อเริ่มต้น</p>
                    </div>
                  </div>
                )}

                {registering && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="text-center text-white">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
                      <p className="text-xl">กำลังลงทะเบียนใบหน้า...</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {!cameraActive && !registrationResult && (
                  <button
                    onClick={startCamera}
                    className="w-full bg-blue-500 text-white py-3 px-6 rounded-md hover:bg-blue-600 flex items-center justify-center space-x-2 transition-colors"
                  >
                    <Camera className="h-6 w-6" />
                    <span>เริ่มลงทะเบียนใบหน้า</span>
                  </button>
                )}

                {cameraActive && !registering && !registrationResult && (
                  <div className="flex space-x-4">
                    <button
                      onClick={registerFace}
                      className="flex-1 bg-green-500 text-white py-3 px-6 rounded-md hover:bg-green-600 flex items-center justify-center space-x-2 transition-colors"
                    >
                      <CheckCircle className="h-6 w-6" />
                      <span>ลงทะเบียน</span>
                    </button>
                    <button
                      onClick={stopCamera}
                      className="bg-gray-500 text-white py-3 px-6 rounded-md hover:bg-gray-600 transition-colors"
                    >
                      ยกเลิก
                    </button>
                  </div>
                )}

                {registrationResult && (
                  <div>
                    <div className={`p-6 rounded-lg ${registrationResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className={`flex items-center space-x-3 ${registrationResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {registrationResult.success ? <CheckCircle className="h-8 w-8" /> : <AlertCircle className="h-8 w-8" />}
                        <p className="text-lg font-semibold whitespace-pre-line">{registrationResult.message}</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={closeFaceModal}
                      className="w-full mt-4 bg-blue-500 text-white py-3 px-6 rounded-md hover:bg-blue-600 transition-colors"
                    >
                      เสร็จสิ้น
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;